import { create } from 'zustand';
import { api } from '../utils/axios-instance';
import { auth } from '../helper/firebaseClient';

export const useDashboardStore = create((set, get) => ({
  // Default widgets list. The actual order will be populated from DB.
  pinnedWidgets: [
    { id: 'dueDate', title: 'Due Date', icon: '📅' },
    { id: 'mailBox', title: 'Email Box', icon: '✉️' },
    { id: 'latestActivity', title: 'Latest Activity', icon: '⚡' },
  ],
  
  availableWidgets: [
    { id: 'notifications', title: 'Notifications', description: 'Company announcements and alerts', icon: '📢' },
    { id: 'calendar', title: 'Calendar', description: 'Manage your schedule and meetings', icon: '📆' },
    { id: 'departmentTaskPulse', title: 'Department Task Pulse', description: 'Metrics and task insights', icon: '📊' },
    { id: 'upcomingEvents', title: 'Upcoming Events', description: 'Team meetings and activities', icon: '🎉' },
    { id: 'accessRequest', title: 'Access Request and Block', description: 'Manage user access and permissions', icon: '🔐' },
    { id: 'storageByDept', title: 'Storage by Department', description: 'Storage usage metrics', icon: '💾' },
    { id: 'todo', title: 'Todo', description: 'Track your tasks filtered by department', icon: '✅' },
  ],
  
  isEditModalOpen: false,

  toggleEditModal: () => set((state) => ({ isEditModalOpen: !state.isEditModalOpen })),

  pinWidget: (widget) => set((state) => {
    const newAvailable = state.availableWidgets.filter(w => w.id !== widget.id);
    const newPinned = [...state.pinnedWidgets, widget];
    state.syncPreferences(newPinned);
    return { availableWidgets: newAvailable, pinnedWidgets: newPinned };
  }),

  unpinWidget: (widgetId) => set((state) => {
    const widget = state.pinnedWidgets.find(w => w.id === widgetId);
    if (!widget) return state;
    
    const newPinned = state.pinnedWidgets.filter(w => w.id !== widgetId);
    const newAvailable = [...state.availableWidgets, widget];
    
    state.syncPreferences(newPinned);
    return { pinnedWidgets: newPinned, availableWidgets: newAvailable };
  }),

  reorderWidgets: (activeId, overId) => set((state) => {
    const oldIndex = state.pinnedWidgets.findIndex(w => w.id === activeId);
    const newIndex = state.pinnedWidgets.findIndex(w => w.id === overId);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      const newPinned = [...state.pinnedWidgets];
      const [item] = newPinned.splice(oldIndex, 1);
      newPinned.splice(newIndex, 0, item);
      state.syncPreferences(newPinned);
      return { pinnedWidgets: newPinned };
    }
    return state;
  }),
  
  syncPreferences: async (pinnedWidgets) => {
    try {
      const token = auth.currentUser && (await auth.currentUser.getIdToken());
      if (!token) return;
      const widgetIds = pinnedWidgets.map(w => w.id);
      await api.put(`/users/update-profile/`, {
        idToken: token,
        profileData: {
          dashboard_widgets: widgetIds
        }
      });
    } catch (err) {
      console.error("Error saving dashboard preferences", err);
    }
  },

  loadPreferences: (widgetIds) => set((state) => {
    if (!Array.isArray(widgetIds) || widgetIds.length === 0) return state;
    
    const allWidgetsMap = {};
    const defaultPinned = [
      { id: 'dueDate', title: 'Due Date', icon: '📅' },
      { id: 'mailBox', title: 'Email Box', icon: '✉️' },
      { id: 'latestActivity', title: 'Latest Activity', icon: '⚡' },
    ];
    
    [...defaultPinned, ...state.availableWidgets, ...state.pinnedWidgets].forEach(w => {
      allWidgetsMap[w.id] = w;
    });

    const newPinned = widgetIds.map(id => allWidgetsMap[id]).filter(Boolean);
    const pinnedSet = new Set(newPinned.map(w => w.id));
    const newAvailable = Object.values(allWidgetsMap).filter(w => !pinnedSet.has(w.id));

    return {
      pinnedWidgets: newPinned,
      availableWidgets: newAvailable
    };
  }),
}));
